import { ConfigurablePropsFromDescription, STYLING_CATEGORY } from '../../../helpers/widget'
import { Override } from "../../../utils/typescript"
import { GeneralWidgetConfig } from "../../WidgetConfiguration"
import { actionButtonSchema, withDefaultActionsSchema } from "../../../shared/action-buttons/action-button-schema"
import z from "zod"
import { WidgetColors } from "../../styling/widget-colors"

const openPopupActionSchema = z.object({
  type: z.literal("open-popup"),
  title: z.string().optional(),
  areaId: z.string(),
  minWidth: z.string().optional(),
  maxWidth: z.string().optional(),
  minHeight: z.string().optional(),
  maxHeight: z.string().optional()
})

const setPerspectiveActionSchema = z.object({
  type: z.literal("set-perspective"),
  areaId: z.string()
})

const toolbarActionsSchema = withDefaultActionsSchema(
  openPopupActionSchema,
  setPerspectiveActionSchema
)
export type ToolbarAction = z.infer<typeof toolbarActionsSchema>

export const toolbarButtonsSchema = z.array(actionButtonSchema(toolbarActionsSchema))
export type ToolbarButtonsConfig = z.infer<typeof toolbarButtonsSchema>

export const configOptions = [
  {
    name: 'sizingVariant',
    category: STYLING_CATEGORY,
    type: 'select',
    label: 'Visual sizing variant',
    options: [
      {
        label: 'Default',
        value: 'default',
      },
      {
        label: 'Large',
        value: 'large',
      },
      {
        label: 'Medium',
        value: 'medium'
      },
      {
        label: 'Compact',
        value: 'compact'
      },
    ]
  },
  {
    name: 'theme',
    category: STYLING_CATEGORY,
    type: 'select',
    label: 'Color theme',
    options: [
      {
        label: 'Light',
        value: 'light',
      },
      {
        label: 'Dark',
        value: 'dark',
      },
      {
        label: 'Wistor',
        value: 'wistor'
      }
    ]
  },
  {
    name: 'buttonsConfig',
    type: 'json-text',
    label: 'Toolbar buttons configuration'
  },
  {
    name: 'customStyling',
    advanced: true,
    category: STYLING_CATEGORY,
    type: 'json-text',
    label: 'Custom styling configuration (CSS-variables)'
  },
  {
    name: 'perspectivesEnabled',
    advanced: true,
    type: 'boolean',
    label: <>Enable perspectives. This will include a configurable widget area below the toolbar. Can be switched via <code>"set-perspective"</code> actions.</>
  },
  {
    name: 'defaultPerspective',
    advanced: true,
    type: 'text',
    label: 'Name of the default perspective'
  },
  {
    name: 'perspectiveTopic',
    advanced: true,
    type: 'text',
    label: 'Topic to publish current perspective to'
  },
  {
    name: 'perspectiveSubscription',
    advanced: true,
    type: 'text',
    label: 'Topic to listen to for perspective changes'
  },
  {
    name: 'areas',
    advanced: true,
    type: 'json',
    label: 'area definitions'
  },
] as const

type ToolbarStyling = WidgetColors & {
  borderBottomWidth?: string
}

export type RawToolbarConfigOptions = ConfigurablePropsFromDescription<typeof configOptions> & GeneralWidgetConfig
export type ToolbarConfigOptions = Override<RawToolbarConfigOptions, {
  buttonsConfig?: ToolbarButtonsConfig | null,
  customStyling?: ToolbarStyling | null,
  _parsingErrors: Error[]
}>
